/**
 * /bookingflight/review — Review Booking (Phase 8).
 *
 * Shows the flight summary, a passenger summary, and a full fare breakdown (base fare
 * per traveller type, taxes, service fee, total). Stores the computed fare in
 * BookingContext and advances to payment.
 */
import React, { useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useBooking } from "../../context/BookingContext";
import FlightMiniSummary from "../../components/booking/FlightMiniSummary";
import { computeFare, formatMoney } from "../../utils/fare";
import "./review-booking.css";

const TYPE_LABEL = { adult: "Adult", child: "Child", infant: "Infant" };

export default function ReviewBooking() {
  const navigate = useNavigate();
  const { searchCriteria, selectedFlight, passengers, contact, setFare } = useBooking();

  const fare = useMemo(
    () => computeFare(selectedFlight, searchCriteria),
    [selectedFlight, searchCriteria]
  );

  // Persist the fare so downstream steps (payment/confirmation) share the exact numbers.
  useEffect(() => {
    if (selectedFlight) setFare(fare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fare, selectedFlight]);

  const proceed = () => {
    setFare(fare);
    navigate("/bookingflight/payment");
  };

  const c = fare.currency;

  return (
    <section className="rv-page">
      <div className="rv-header">
        <div className="rv-header__inner">
          <button type="button" className="rv-back" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back
          </button>
          <h1 className="rv-title">Review Your Booking</h1>
          <p className="rv-subtitle">Confirm the details below before payment.</p>
        </div>
      </div>

      <div className="rv-body">
        <div className="rv-main">
          {/* Flight */}
          <div className="rv-block">
            <div className="rv-block__head">
              <h2><i className="fas fa-plane-departure" aria-hidden="true" /> Flight Summary</h2>
              <Link to="/bookingflight/results" className="rv-edit">Change flight</Link>
            </div>
            <FlightMiniSummary flight={selectedFlight} criteria={searchCriteria} />
          </div>

          {/* Passengers */}
          <div className="rv-block">
            <div className="rv-block__head">
              <h2><i className="fas fa-users" aria-hidden="true" /> Passengers ({passengers.length})</h2>
              <Link to="/bookingflight/passenger" className="rv-edit">Edit</Link>
            </div>
            <div className="rv-pax-list">
              {passengers.map((p, i) => (
                <div className="rv-pax" key={i}>
                  <div className="rv-pax__avatar"><i className="fas fa-user" aria-hidden="true" /></div>
                  <div className="rv-pax__info">
                    <span className="rv-pax__name">
                      {p.fullName}
                      <span className="rv-pax__type">{TYPE_LABEL[p.type]}</span>
                    </span>
                    <span className="rv-pax__meta">
                      {p.gender} · {p.nationality} · Passport {p.passportNumber}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {contact && (
              <div className="rv-contact">
                <span><i className="fas fa-envelope" aria-hidden="true" /> {contact.email}</span>
                <span><i className="fas fa-phone" aria-hidden="true" /> {contact.phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Fare breakdown */}
        <aside className="rv-aside">
          <div className="rv-fare">
            <h2 className="rv-fare__title">Fare Breakdown</h2>

            <div className="rv-fare__rows">
              <div className="rv-fare__row">
                <span>Adults ({fare.adults} × {formatMoney(fare.perFare, c)})</span>
                <span>{formatMoney(fare.adultTotal, c)}</span>
              </div>
              {fare.children > 0 && (
                <div className="rv-fare__row">
                  <span>Children ({fare.children} × {formatMoney(fare.perFare, c)})</span>
                  <span>{formatMoney(fare.childTotal, c)}</span>
                </div>
              )}
              {fare.infants > 0 && (
                <div className="rv-fare__row">
                  <span>Infants ({fare.infants} × {Math.round(fare.infantRate * 100)}%)</span>
                  <span>{formatMoney(fare.infantTotal, c)}</span>
                </div>
              )}

              <div className="rv-fare__row rv-fare__row--sub">
                <span>Base Fare</span>
                <span>{formatMoney(fare.baseFare, c)}</span>
              </div>
              <div className="rv-fare__row">
                <span>Taxes &amp; Surcharges</span>
                <span>{formatMoney(fare.taxes, c)}</span>
              </div>
              <div className="rv-fare__row">
                <span>Service Fee</span>
                <span>{formatMoney(fare.serviceFee, c)}</span>
              </div>
            </div>

            <div className="rv-fare__total">
              <span>Total Amount</span>
              <span className="rv-fare__total-value">{formatMoney(fare.total, c)}</span>
            </div>

            <button type="button" className="rv-pay-btn" onClick={proceed}>
              Proceed to Payment <i className="fas fa-arrow-right" aria-hidden="true" />
            </button>
            <p className="rv-fare__note">
              <i className="fas fa-lock" aria-hidden="true" /> Mock checkout — no real payment is taken.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
